import { useId, useRef, useState } from 'react'

import { chartAssets } from '../../constants/assets'
import { revenueRanges, type RevenueRange } from '../../data/revenue'
import { useClickOutside } from '../../hooks/useClickOutside'
import { cn } from '../../utils/cn'

type RevenueRangeSelectProps = {
  value: RevenueRange
  onChange: (value: RevenueRange) => void
}

export const RevenueRangeSelect = ({ value, onChange }: RevenueRangeSelectProps) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useClickOutside(containerRef, () => setOpen(false), open)

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex h-[35px] w-[87px] items-center rounded-[6px] border border-solid border-dash-hairline px-[10px] transition-colors hover:border-white/40 motion-reduce:transition-none"
      >
        <span className="flex w-full items-center justify-between gap-px">
          <span className="whitespace-nowrap font-host text-[13px] font-bold leading-[10px] text-dash-chart-label">
            {value}
          </span>
          <img
            alt=""
            aria-hidden
            className={cn(
              'block size-[24px] max-w-none transition-transform duration-200 motion-reduce:transition-none',
              open && 'rotate-180',
            )}
            src={chartAssets.rangeCaret}
          />
        </span>
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Revenue range"
          className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[108px] overflow-hidden rounded-[8px] border border-solid border-dash-hairline bg-dash-panel py-[4px] shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
        >
          {revenueRanges.map((option) => (
            <li key={option} role="option" aria-selected={option === value}>
              <button
                type="button"
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full px-[12px] py-[8px] text-left font-host text-[13px] font-bold leading-[10px] transition-colors motion-reduce:transition-none',
                  option === value
                    ? 'bg-white/10 text-white'
                    : 'text-dash-chart-label hover:bg-white/5 hover:text-white',
                )}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
